import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Button, EmptyState, ErrorState, Panel, Skeleton } from '@/design/components';
import { useImageUrl } from '@/features/products/api';
import { cn } from '@/lib/cn';
import { formatDate, formatDateTime } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { useOpsPhoto } from '../photos';
import { useDisplayMutations, useReviewQueue, type QueueItem } from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/** מסגרת בגודל קבוע, כדי ששתי התמונות יהיו ברות השוואה. */
function Frame({ caption, children }: { caption: string; children: React.ReactNode }) {
  return (
    <figure className="m-0 grid gap-1">
      <figcaption className="text-label font-medium text-muted">{caption}</figcaption>
      <div className="grid h-56 place-items-center overflow-hidden rounded-sm border border-line bg-cream p-1">
        {children}
      </div>
    </figure>
  );
}

/**
 * תור האישורים: כל מה שממתין, מכל הסידורים (מיגרציה 067).
 *
 * שתי התמונות זו לצד זו, כי השיפוט הוא השוואה. כשהיעד היה בעמוד
 * אחר ההחלטה נשענה על הזיכרון.
 */
export function ReviewQueue() {
  const queue = useReviewQueue();

  if (queue.isPending) return <Skeleton className="h-64" />;
  if (queue.isError) return <ErrorState onRetry={() => void queue.refetch()} />;

  const rows = queue.data ?? [];

  if (rows.length === 0) {
    return (
      <EmptyState
        title="אין מה לאשר"
        description="כל מה שהסניפים הגישו כבר נבדק."
      />
    );
  }

  return (
    <div className="grid gap-3">
      <p className="m-0 text-body-sm text-muted">
        <span className="tabular">{rows.length}</span> הגשות ממתינות, מהוותיקה לחדשה.
      </p>

      {rows.map((item) => (
        <QueueCard key={item.id} item={item} />
      ))}
    </div>
  );
}

function QueueCard({ item }: { item: QueueItem }) {
  const { review } = useDisplayMutations();
  const reference = useImageUrl(item.reference_image_path);
  const photo = useOpsPhoto(item.photo_path);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);
  const isLate = Boolean(item.campaign_due_date && item.campaign_due_date < today);

  const decide = (approve: boolean) => {
    setError(null);
    review
      .mutateAsync({ id: item.id, approve, feedback: feedback.trim() || undefined })
      .catch((caught: unknown) => setError(errorMessage(caught)));
  };

  return (
    <Panel
      title={item.campaign_title}
      actions={
        <span
          className="inline-flex min-h-8 items-center rounded-pill border border-line px-3 text-body-sm font-medium text-ink"
          style={item.branch_color ? { backgroundColor: item.branch_color } : undefined}
        >
          {item.branch_name}
        </span>
      }
    >
      <div className="grid gap-3">
        <p className="m-0 text-label text-muted">
          {item.submitted_by && `${item.submitted_by} · `}
          {formatDateTime(item.created_at)}
          {item.campaign_due_date && (
            <>
              {' · '}
              <span className={cn(isLate && 'font-semibold text-bad')}>
                {isLate && '⚠ '}
                עד {formatDate(item.campaign_due_date)}
              </span>
            </>
          )}
        </p>

        {item.instructions && (
          <p className="m-0 whitespace-pre-line text-body-sm">{item.instructions}</p>
        )}

        {/* היעד וההגשה זה לצד זה, בשתי מסגרות באותו גודל. זו כל
            ההחלטה, והשוואה בין מסגרות בגדלים שונים אינה השוואה. */}
        <div className="grid gap-3 md:grid-cols-2">
          <Frame caption="מה ביקשנו">
            {reference.data ? (
              <img src={reference.data} alt="" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-label text-muted">לא צורפה תמונת יעד</span>
            )}
          </Frame>

          <Frame caption="מה הסניף העלה">
            {photo.data && (
              <img src={photo.data} alt="" className="max-h-full max-w-full object-contain" />
            )}
            {/* בלי ענף השגיאה צילום שלא נטען היה שלד שמסתובב
                לנצח, ומי שמאשר לא היה יודע אם לחכות */}
            {!photo.data && photo.isError && (
              <span className="text-label text-muted">הצילום לא נטען.</span>
            )}
            {!photo.data && !photo.isError && <Skeleton className="h-full w-full" />}
          </Frame>
        </div>

        <input
          value={feedback}
          onChange={(event) => setFeedback(event.target.value)}
          placeholder="הערה לסניף, לא חובה"
          aria-label={`הערה ל${item.branch_name}`}
          className={FIELD}
        />

        <div className="flex flex-wrap gap-2">
          <Button variant="primary" disabled={review.isPending} onClick={() => decide(true)}>
            <Check size={18} strokeWidth={1.75} aria-hidden="true" />
            אישור
          </Button>
          <Button variant="secondary" disabled={review.isPending} onClick={() => decide(false)}>
            <X size={18} strokeWidth={1.75} aria-hidden="true" />
            החזרה לתיקון
          </Button>
        </div>

        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}
      </div>
    </Panel>
  );
}
